import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { FaSearch, FaEye, FaPhone, FaEnvelope, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import AdminNav from '../../components/AdminNav';
import api from '../../utils/api';
import './AdminRepairs.css';

const AdminRepairs = () => {
  const { user } = useContext(AuthContext);
  const [repairs, setRepairs] = useState([]);
  const [filteredRepairs, setFilteredRepairs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedRepair, setSelectedRepair] = useState(null);

  useEffect(() => {
    fetchRepairs();
  }, []);

  useEffect(() => {
    let filtered = repairs;

    if (searchTerm) {
      filtered = filtered.filter(repair =>
        repair.contactInfo?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        repair.contactInfo?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        repair.contactInfo?.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        repair._id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(repair => repair.status === statusFilter);
    }

    setFilteredRepairs(filtered);
  }, [searchTerm, statusFilter, repairs]);

  const fetchRepairs = async () => {
    try {
      const response = await api.get('/repairs');
      if (response.data.success) {
        setRepairs(response.data.repairRequests);
        setFilteredRepairs(response.data.repairRequests);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching repairs:', error);
      setLoading(false);
    }
  };

  const updateRepairStatus = async (repairId, newStatus) => {
    try {
      await api.put(`/repairs/${repairId}/status`, { status: newStatus });
      fetchRepairs();
      if (selectedRepair && selectedRepair._id === repairId) {
        setSelectedRepair({ ...selectedRepair, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating repair status:', error);
      alert('Failed to update repair status');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-badge pending',
      confirmed: 'status-badge confirmed',
      'in-progress': 'status-badge in-progress',
      completed: 'status-badge completed',
      cancelled: 'status-badge cancelled'
    };
    return <span className={statusClasses[status]}>{status}</span>;
  };

  if (!user || !user.isAdmin) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return <div className="admin-loading">Loading repair requests...</div>;
  }

  return (
    <>
      <AdminNav />
      <div className="admin-repairs">
        <div className="admin-header">
          <div>
            <h1>Repair Requests</h1>
            <p>{repairs.length} total requests</p>
          </div>
        </div>

        <div className="repairs-controls">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search by customer name, email, phone, or request ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Requests</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="repairs-table">
          <table>
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Customer</th>
                <th>Furniture Type</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRepairs.map(repair => (
                <tr key={repair._id}>
                  <td><strong>#{repair._id.substring(0, 8)}</strong></td>
                  <td>
                    <div>{repair.contactInfo?.name}</div>
                    <div className="email-text">{repair.contactInfo?.phone}</div>
                  </td>
                  <td>{repair.furnitureType}</td>
                  <td>{new Date(repair.createdAt).toLocaleDateString()}</td>
                  <td>{getStatusBadge(repair.status)}</td>
                  <td>
                    <button
                      onClick={() => setSelectedRepair(repair)}
                      className="btn-icon btn-view"
                    >
                      <FaEye /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRepairs.length === 0 && (
            <div className="no-results">
              No repair requests found.
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedRepair && (
        <div className="modal-overlay" onClick={() => setSelectedRepair(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Repair Request Details</h2>
              <button className="close-btn" onClick={() => setSelectedRepair(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {/* Request Information */}
              <div className="detail-section">
                <h3>Request Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Request ID:</label>
                    <span>#{selectedRepair._id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date Submitted:</label>
                    <span>{new Date(selectedRepair.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>Furniture Type:</label>
                    <span>{selectedRepair.furnitureType}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <div className="status-update">
                      {getStatusBadge(selectedRepair.status)}
                      <select
                        value={selectedRepair.status}
                        onChange={(e) => updateRepairStatus(selectedRepair._id, e.target.value)}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Contact Information */}
              <div className="detail-section">
                <h3>Customer Contact Information</h3>
                <div className="contact-info">
                  <div className="contact-item">
                    <FaPhone className="contact-icon" />
                    <div>
                      <label>Phone:</label>
                      <a href={`tel:${selectedRepair.contactInfo?.phone}`}>
                        {selectedRepair.contactInfo?.phone}
                      </a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <FaEnvelope className="contact-icon" />
                    <div>
                      <label>Email:</label>
                      <a href={`mailto:${selectedRepair.contactInfo?.email}`}>
                        {selectedRepair.contactInfo?.email}
                      </a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <FaMapMarkerAlt className="contact-icon" />
                    <div>
                      <label>Address:</label>
                      <span>
                        {selectedRepair.contactInfo?.address}, {selectedRepair.contactInfo?.zipCode}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Complaint Nature */}
              <div className="detail-section">
                <h3>Complaint Nature</h3>
                <div className="issues-list">
                  {selectedRepair.issues && selectedRepair.issues.map((issue, index) => (
                    <span key={index} className="issue-tag">{issue}</span>
                  ))}
                </div>
                {selectedRepair.additionalDetails && (
                  <div className="additional-details">
                    <label>Additional Details:</label>
                    <p>{selectedRepair.additionalDetails}</p>
                  </div>
                )}
              </div>

              {/* Dimensions */}
              {selectedRepair.dimensions && (
                <div className="detail-section">
                  <h3>Furniture Dimensions</h3>
                  <div className="dimensions-grid">
                    <div className="dimension-item">
                      <label>Width:</label>
                      <span>{selectedRepair.dimensions.width || 'N/A'}</span>
                    </div>
                    <div className="dimension-item">
                      <label>Height:</label>
                      <span>{selectedRepair.dimensions.height || 'N/A'}</span>
                    </div>
                    <div className="dimension-item">
                      <label>Depth:</label>
                      <span>{selectedRepair.dimensions.depth || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Images */}
              {selectedRepair.photos && selectedRepair.photos.length > 0 && (
                <div className="detail-section">
                  <h3>Uploaded Images ({selectedRepair.photos.length})</h3>
                  <div className="photos-grid">
                    {selectedRepair.photos.map((photo, index) => (
                      <div key={index} className="photo-item">
                        <img src={photo} alt={`Repair ${index + 1}`} />
                        <a href={photo} target="_blank" rel="noopener noreferrer" className="view-full">
                          View Full Size
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Estimated Cost */}
              {selectedRepair.estimatedCost && (
                <div className="detail-section">
                  <h3>Estimated Cost</h3>
                  <p className="estimated-cost">${selectedRepair.estimatedCost.toFixed(2)}</p>
                </div>
              )}

              {/* Notes */}
              {selectedRepair.notes && (
                <div className="detail-section">
                  <h3>Admin Notes</h3>
                  <p>{selectedRepair.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminRepairs;
